import { watch } from 'vue'
import { useProblemPresentation } from '~/composables/useProblemPresentation'
import type { AppProblemModel } from '~/services/api/AppProblemModel'
import type { FieldServerMessage } from '~/types/FieldServerMessage'
import type { FormFieldMap } from '~/services/forms/FormFieldMap'
import { FormProblemState } from '~/services/forms/FormProblemState'

interface ServerProblemSyncOptions<TField extends string> {
  readonly failureMessage: () => string
  readonly fieldMap: FormFieldMap<TField>
  readonly problem: () => AppProblemModel | null
  readonly setFieldServerErrors: (field: TField, messages: FieldServerMessage[]) => void
  readonly setProblem: (state: FormProblemState | null) => void
}

/** Mirrors a server-side submit failure into field errors and the form-level problem state, dropping the previous failure's field errors first. */
export const useServerProblemSync = <TField extends string>(
  options: ServerProblemSyncOptions<TField>
): void => {
  const { t } = useI18n()
  const problemPresentation = useProblemPresentation()

  // Synchronous so a cleared problem never lands after a verdict the same attempt raises next.
  watch(
    options.problem,
    (serverProblem) => {
      problemPresentation.clearProblemFieldErrors(options.setFieldServerErrors, options.fieldMap)
      if (serverProblem === null) {
        options.setProblem(null)
        return
      }

      const { unmapped } = problemPresentation.applyProblemFieldErrors(
        options.setFieldServerErrors,
        serverProblem,
        options.fieldMap,
        (issue) => problemPresentation.fieldIssueMessage(issue, t)
      )
      options.setProblem(
        FormProblemState.fromFailure(serverProblem, unmapped, {
          issue: (issue) => problemPresentation.fieldIssueMessage(issue, t),
          problem: (failedProblem) =>
            problemPresentation.resolveMessage(failedProblem, t, options.failureMessage())
        })
      )
    },
    { flush: 'sync' }
  )
}
